import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Slide from "@mui/material/Slide";
import Toolbar from "@mui/material/Toolbar";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import EventBus from "../services/event-bus";
import useAlerts from "../hooks/useAlerts";
import Logo from '../assets/images/logo-navbar.svg?react';
import {Button, Grid2 as Grid, Avatar, Typography, Badge, Chip} from "@mui/material";
import { deepPurple } from '@mui/material/colors';
import useWipeData from "../hooks/useWipeData.js";
import {generateShortname, getAppVersion} from "../helpers";
import {useNavigate} from "react-router";
import {ROUTES} from "../routes/config.jsx";

// Nasconde AppBar quando lo scorri verso il basso
const HideOnScroll = ({children}) => {
    const trigger = useScrollTrigger();
    return (
        <Slide appear={false} direction="down" in={!trigger}>
            {children}
        </Slide>
    );
};

export const MenuAppBar = (props) => {
    const { user, onLogout } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const dispatchAlert = useAlerts();
    const wipeData = useWipeData();
    const navigate = useNavigate();

    /* eslint-disable */
    useEffect(() => {
        // Logout automatico per token scaduto naturalmente
        EventBus.subscribe('JWT_TOKEN_EXPIRED', () => onLogout());

        // Logout automatico per sessione invalidata da altro device
        EventBus.subscribe('SESSION_INVALIDATED', () => {
            dispatchAlert('Sei stato disconnesso perché hai effettuato l\'accesso da un altro dispositivo', {
                variant: 'warning',
                preventDuplicate: true
            });
            onLogout();
        });

        EventBus.subscribe('DAILY_LOGOUT', () => {
            dispatchAlert('Sessione terminata: effettua nuovamente il login', {
                variant: 'info',
                preventDuplicate: true
            });
            onLogout();
        });
    }, []);
    /* eslint-enable */

    // Set the anchor for the menu to the trigger element
    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleReset = async () => {
        await wipeData(() => {
            handleClose();
            onLogout();
        });
    };

    const handlePickingStarted = async () => {
        handleClose();
        navigate(ROUTES.PICKING_STARTED.path);
    };

    useEffect(() => {
        EventBus.subscribe('CRITICAL_ERROR', (event) => dispatchAlert(
            `Si è verificato un errore critico sul server, segnalare al team tecnico: ${event.detail?.message}`, {
                variant: "error",
                preventDuplicate: true
            }
        ))

        EventBus.subscribe('NO_CONNECTION', () => dispatchAlert(
            'Si è verificato un problema di connessione', {
                variant: "error",
                preventDuplicate: true
            }
        ))

        EventBus.subscribe('CONSTRAINT_VIOLATIONS', event => {
            if (event.detail.message) {
                dispatchAlert(event.detail.message, {
                    variant: "error",
                    preventDuplicate: true
                });
            }
        })

        // Alert per login rifiutato (opzionale, già gestito nel catch)
        EventBus.subscribe('ALREADY_LOGGED_IN', (event) => {
            dispatchAlert(event.detail?.message || 'Sei già loggato da un altro dispositivo', {
                variant: "warning",
                preventDuplicate: true
            });
        })
    }, [dispatchAlert]);

    return (
        <Box sx={{ flexGrow: 1 }} id="top-bar">
            <HideOnScroll {...props}>
                <AppBar
                    position="fixed"
                    sx={{ zIndex: (theme) => theme.zIndex.drawer + 1,  height: '40px', }}
                >
                    <Toolbar variant={"dense"} sx={{minHeight: '40px'}}>
                        <Grid container sx={{ alignItems: "center", justifyContent: "space-between", width: '100%'}}>
                            {/* Logo e scritta allineati a sinistra */}
                            <Grid size={7} sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 1 }}>
                                <Logo style={{ height: 24, width: 24, fill: "white" }} />
                                <Typography variant="subtitle1" component="div" sx={{ fontWeight: 500 }}>
                                    LogiWare
                                </Typography>
                                <Chip
                                    label={getAppVersion()}
                                    size="small"
                                    color="secondary"
                                />
                            </Grid>

                            {/* Spazio centrale vuoto */}
                            <Grid size={2} sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            </Grid>

                            {/* Avatar allineato a destra */}
                            <Grid size={3} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
                                {user && (
                                    <>
                                        <Button
                                            size="small"
                                            aria-label="account of current user"
                                            aria-controls="menu-appbar"
                                            aria-haspopup="true"
                                            onClick={handleMenu}
                                            color="inherit"
                                            className="text-truncated"
                                            sx={{justifyContent: 'flex-end'}}
                                        >
                                            <Chip
                                                label={generateShortname(user.username)}
                                                size="medium"
                                                sx={{
                                                    bgcolor: deepPurple[500],
                                                    color: 'white',
                                                    textTransform: 'none',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.9rem',
                                                }}
                                            />
                                        </Button>
                                        <Menu
                                            id="menu-appbar"
                                            anchorEl={anchorEl}
                                            anchorOrigin={{
                                                vertical: "top",
                                                horizontal: "right",
                                            }}
                                            keepMounted
                                            transformOrigin={{
                                                vertical: "top",
                                                horizontal: "right",
                                            }}
                                            open={Boolean(anchorEl)}
                                            onClose={handleClose}
                                        >
                                            {user?.roles?.includes('ROLE_ADMIN') && (
                                                <MenuItem onClick={() => {
                                                    handleClose();
                                                    navigate('/admin/sessions');
                                                }}>
                                                    Gestione Sessioni
                                                </MenuItem>
                                            )}
                                            <MenuItem
                                                onClick={(e)=> handlePickingStarted(e)}
                                            >
                                                Picking Avviati
                                            </MenuItem>
                                            <MenuItem
                                                onClick={(e)=> handleReset(e)}
                                            >
                                                Cancella Memoria
                                            </MenuItem>
                                            <MenuItem
                                                onClick={(e)=> onLogout(e) && handleClose}
                                            >
                                                Logout
                                            </MenuItem>
                                        </Menu>
                                    </>
                                )}
                            </Grid>
                        </Grid>
                    </Toolbar>
                </AppBar>
            </HideOnScroll>
        </Box>
    );
};
